import { useEffect, useMemo, useState } from 'react';
import type { BatchStatus, BatchTask, ReplaceKind } from '@avs/contract';
import { useAppState } from '../state/AppContext.js';
import { useApiPolled } from '../hooks/usePolling.js';
import { showToast } from '../hooks/useToasts.js';
import { apiClient } from '../api/client.js';
import { Icon } from '../icons/Icon.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { StatusTag } from '../components/Tag.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { EmptyState } from '../components/EmptyState.js';
import { PrefixInput, NativeSelect, Toolbar } from '../components/Toolbar.js';
import { ReplaceWizard } from '../components/wizard/ReplaceWizard.js';
import { BatchDetailDrawer } from '../components/BatchDetailDrawer.js';
import { relativeTime } from '../utils/time.js';
import {
  KIND_LABEL, KIND_DESC, SEARCH_PLACEHOLDER,
} from '../utils/kinds.js';

type StatusFilter = 'all' | BatchStatus;

const STATUS_OPTIONS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: '全部状态' },
  { value: 'processing', label: '处理中' },
  { value: 'done', label: '已完成' },
  { value: 'failed', label: '失败' },
  { value: 'draft', label: '草稿' },
];

export function Replace() {
  const { route } = useAppState();
  if (route !== 'object' && route !== 'scene' && route !== 'person') return null;
  return <ReplaceInner kind={route} />;
}

function ReplaceInner({ kind }: { kind: ReplaceKind }) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [outputFilter, setOutputFilter] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  // 有 processing 就开轮询；状态由 result 推导
  const [shouldPoll, setShouldPoll] = useState(false);
  const { data, loading, refresh } = useApiPolled(
    () => apiClient.listBatches({
      kind,
      status: status === 'all' ? undefined : status,
      search: search || undefined,
      outputLocation: outputFilter || undefined,
      limit: 50,
    }),
    3000,
    [kind, status, search, outputFilter],
    shouldPoll,
  );

  const rows = data?.items ?? [];
  // 根据当前数据自动决定要不要轮询（带 ref-eq 比较，避免无谓 setState）
  useEffect(() => {
    const next = rows.some((b) => b.status === 'processing');
    setShouldPoll((cur) => cur === next ? cur : next);
  }, [rows]);

  const columns = useMemo<ReadonlyArray<Column<BatchTask>>>(() => [
    {
      key: 'task',
      header: '任务',
      cell: (r) => (
        <div className="cell-clickable" onClick={() => setDetailId(r.id)} role="button" tabIndex={0}>
          <div className="cell-title">{r.name}</div>
          <div className="cell-sub">{r.id} · 策略「{r.strategy}」· {r.params.resolution}</div>
        </div>
      ),
    },
    { key: 'status', header: '状态', cell: (r) => <StatusTag status={r.status} /> },
    {
      key: 'jobs',
      header: 'Job 分布',
      cell: (r) => (
        <span className="jobs-bar">
          <span className="jdot done" />{r.jobs.done}
          <span className="jdot run" style={{ marginLeft: 8 }} />{r.jobs.running}
          <span className="jdot fail" style={{ marginLeft: 8 }} />{r.jobs.failed}
        </span>
      ),
    },
    { key: 'kind', header: '处理类型', cell: (r) => KIND_LABEL[r.kind] },
    {
      key: 'output',
      header: '输出位置',
      cell: (r) => <span className="mono-sm" title={r.outputLocation}>{r.outputLocation || '—'}</span>,
    },
    { key: 'time', header: '时间', cell: (r) => relativeTime(r.updatedAt) },
    {
      key: 'ops',
      header: '操作',
      width: 100,
      cell: (r) => (
        <span className="cell-ops">
          <button
            type="button"
            className="icon-btn"
            title="详情"
            onClick={() => setDetailId(r.id)}
          >
            <Icon name="arrowRight" size={16} />
          </button>
          {(r.status === 'failed' || r.status === 'done') && (
            <button
              type="button"
              className="icon-btn"
              title="重试"
              onClick={async () => {
                try {
                  await apiClient.retryBatch({ id: r.id });
                  showToast('已重试：' + r.name, 'ok');
                  refresh();
                } catch (e) {
                  showToast(e instanceof Error ? e.message : '重试失败', 'err');
                }
              }}
            >
              <Icon name="refresh" size={16} />
            </button>
          )}
        </span>
      ),
    },
  ], [refresh]);

  return (
    <>
      <div className="subtitle-row">
        <div className="subtitle">{KIND_DESC[kind]}</div>
        <Button
          variant="primary"
          leftIcon={<Icon name="add" size={16} />}
          onClick={() => setWizardOpen(true)}
        >新建</Button>
      </div>

      <Card>
        <Toolbar>
          <PrefixInput
            icon="search"
            placeholder={SEARCH_PLACEHOLDER[kind]}
            value={search}
            onChange={setSearch}
            width={260}
          />
          <PrefixInput
            icon="folder"
            placeholder="筛选输出位置"
            value={outputFilter}
            onChange={setOutputFilter}
            width={200}
          />
          <NativeSelect
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
            width={140}
          />
          <PrefixInput
            icon="history"
            placeholder="创建时间范围"
            value=""
            onChange={() => { /* Slice 4 接日期 */ }}
            width={180}
          />
          <div className="grow" />
          <Button
            leftIcon={<Icon name="refresh" size={16} />}
            onClick={() => { refresh(); showToast('已刷新'); }}
          >刷新</Button>
        </Toolbar>

        <div style={{ marginTop: 'var(--space-s)' }}>
          {loading && rows.length === 0 ? (
            <EmptyState icon="refresh" title="加载中" sub="正在拉取批次…" />
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(r) => r.id}
              empty={
                <EmptyState
                  icon="search"
                  title={`暂无${KIND_LABEL[kind]}任务`}
                  sub="调整筛选条件，或点击右上角「新建」开始一个批次。"
                  variant="plain"
                />
              }
            />
          )}
        </div>
      </Card>

      {wizardOpen && (
        <ReplaceWizard
          kind={kind}
          onClose={() => setWizardOpen(false)}
          onDone={() => { refresh(); }}
        />
      )}
      <BatchDetailDrawer
        open={detailId !== null}
        batchId={detailId}
        onClose={() => setDetailId(null)}
        onMutated={() => refresh()}
      />
    </>
  );
}
